import {AfterViewInit, Component, inject, Input, input, OnDestroy, OnInit, output, signal} from '@angular/core';
import {OrderProcessingService} from "../../../../data/services/order-processing.service";
import {TranslationService} from "../../../../core/services/translation.service";
import {ConfigService} from "../../../../core/services/config.service";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatStepper} from "@angular/material/stepper";
import {GeneralWarningComponent} from "../../../../core/components/general-warning/general-warning.component";
import {
  HupSubscriptionAddressComponent
} from "../../../hup-subscription/hup-subscription-detail/hup-subscription-address/hup-subscription-address.component";
import {MatError, MatFormField, MatLabel} from "@angular/material/input";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatIcon} from "@angular/material/icon";
import {GtmEnum} from "../../../../core/models/gtm.enum";
import {UserData} from "../../../../data/models/user-data.model";
import {OrderTrackingService} from "../../../../core/services/order-tracking.service";
import {ValidationPattern} from "../../../../core/models/ValidationPattern";
import {Offer} from "../../../../data/models/offer.model";
import {AddressCode} from "../../../../data/models/address-code.model";
import {ClientType} from "../../../../data/models/client-type.model";
import {BaseDataService} from "../../../../core/services/base-data.service";
import {CustomValidators} from "../../../../core/validators";
import {HttpClient} from "@angular/common/http";
import {MatButton} from "@angular/material/button";
import {AddressCombination} from "../../../../data/models/address-combination";
import {AddressFormValue} from "../../../../data/models/subscription-order.model";
import {distinctUntilChanged, map} from "rxjs/operators";
import {Subscription} from "rxjs";
import {HouseNo} from "../../../../data/models/houseno/houseno";

/**
 * Die Inhalts-Gruppen des Rechnungs-Adressblocks, die mit dem Auf-/Zuklappen aktiviert bzw. deaktiviert
 * werden. Das Schalter-Control 'showAddress' gehört bewusst nicht dazu — s. applyBillingAddressActivation().
 */
const BILLING_CONTENT_GROUPS = ['address', 'person', 'userData'];

@Component({
  selector: 'app-address-step',
  imports: [
    ReactiveFormsModule,
    GeneralWarningComponent,
    HupSubscriptionAddressComponent,
    MatFormField,
    MatLabel,
    MatProgressSpinner,
    MatIcon,
    MatError,
    MatButton
  ],
  templateUrl: './address-step.component.html',
  styleUrl: './address-step.component.css'
})
export class AddressStepComponent implements OnInit, AfterViewInit, OnDestroy {
  //Services
  protected orderProcessService = inject(OrderProcessingService);
  protected translationService = inject(TranslationService);
  protected configService = inject(ConfigService);
  protected orderTrackingService = inject(OrderTrackingService);
  protected baseDataService = inject(BaseDataService);
  protected http = inject(HttpClient);

  public userData = signal<UserData>(null);
  public hideLoginBox = input<boolean>();
  public isWinBack = input<boolean>();
  public isEditable = input<boolean>();
  public currentProductAlreadyOrdered = input<boolean>();
  public bonusSelection = input<boolean>();
  public offer = input.required<Offer>();

  //Signals sorgen dafür, dass abhängige UI-Teile automatisch aktualisiert werden, wenn sich ein Wert ändert.
  protected askForPhone = signal<boolean>(false);
  protected isPhoneRequired = signal<boolean>(false);
  protected askForBirthday = signal<boolean>(false);
  protected askForBirthdayConst = signal<boolean>(false);
  protected isBirthdayRequired = signal<boolean>(false); // Dieser Wert hängt von der Konfig 'requiredFields.addr.birthday' oder von checkCreditRequired-Property des Bonus ab
  protected isBirthdayRequiredConst = signal<boolean>(false); // Dieser Wert hängt von der Konfig 'requiredFields.addr.birthday' oder von checkCreditRequired-Property des Bonus ab
  protected askForEmail = signal<boolean>(true);
  protected showWarningForOrderWithoutAddress = signal<boolean>(false);
  protected clientWithoutAddressAllowed = signal<boolean>(false);

  public addressCombination = input(AddressCombination.BOTH);
  public deliveryAddress = signal({});
  public billingAddress = signal({})

  @Input() parentStepper!: MatStepper;

  private formBuilder = new FormBuilder();
  protected addressFormGroup: FormGroup;

  protected salutations: AddressCode[] = [];
  protected clientTypes: ClientType[] = [];

  stepCompleted = output<boolean>();
  private readonly subscriptions = new Subscription();

  private isPrefilled = false;

  ngOnInit(): void {
    this.handleBaseData();
    this.handleConfigurations();
    this.buildAddressForm();
    this.connectFormWithOrderService();

    this.subscriptions.add(this.addressFormGroup.statusChanges.subscribe(status => {
      this.stepCompleted.emit(status === 'VALID');
    }));

    this.subscriptions.add(this.addressFormGroup.get('billingAddress.showAddress')
      ?.valueChanges.subscribe(() => this.applyBillingAddressActivation()));

    this.applyBillingAddressActivation();
    this.announceBillingAddressRequest();
    // statusChanges liefert nur Status-Übergänge. Der Parent-Step braucht aber auch dann einen Wert,
    // wenn das Formular von Anfang an valide ist (z.B. Bestandskunde mit readonly-Lieferadresse).
    this.stepCompleted.emit(this.addressFormGroup.status === 'VALID');
  }

  /**
   * Die Pflichtfelder hängt erst HupSubscriptionAddressComponent über [required] im eigenen Template an.
   * Angular rechnet die Gültigkeit dabei ohne statusChanges neu - der Stand aus ngOnInit (leeres
   * Formular ohne Validatoren, also VALID) bliebe sonst stehen, und der Schritt galt schon vor der
   * ersten Eingabe als erledigt.
   */
  ngAfterViewInit(): void {
    this.stepCompleted.emit(this.addressFormGroup.status === 'VALID');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // Der Step verschwindet aus der Strecke - ab jetzt darf die Zusammenfassung die Rechnungsadresse
    // wieder selbst abfragen.
    this.setBillingAddressAskedHere(false);
  }

  /**
   * Meldet dem Bestell-State, dass die Rechnungsadresse hier abgefragt wird. Spätere Schritte fragen
   * sie dann nicht noch einmal ab - s. OrderState.billingAddressAskedInAddressStep.
   *
   * Maßgeblich ist die gerenderte Maske, nicht die Konfiguration: Bei ONLY_BILLING steht sie offen,
   * bei BOTH hinter "Abweichende Rechnungsadresse", bei ONLY_DELIVERY gibt es sie nicht.
   */
  private announceBillingAddressRequest(): void {
    this.setBillingAddressAskedHere(
      this.addressCombination() === AddressCombination.BOTH
      || this.addressCombination() === AddressCombination.ONLY_BILLING);
  }

  private setBillingAddressAskedHere(asked: boolean): void {
    this.orderProcessService.update(state => ({
      ...state,
      billingAddressAskedInAddressStep: asked
    }));
  }

  private connectFormWithOrderService() {
    this.subscriptions.add(this.orderProcessService.getOrderState()
      .pipe(
        map(state => state?.userData),
        distinctUntilChanged()
      )
      .subscribe(value => {

        this.userData.set(value);
        this.deliveryAddress.set(value);
        this.billingAddress.set(value);

        this.prefillAddressForm(value);
      }));


    // Rohwert nur, solange der Block aufgeklappt ist: Dann ist er entweder bearbeitbar (Rohwert
    // gleich Formularwert) oder er ist die readonly-Maske eines Bestandskunden und nur der Rohwert
    // enthält dessen vorbefüllte Adresse. Zugeklappt wird der Block nicht übertragen - er ist
    // fachlich nicht vorhanden (P5W-4409); an seine Stelle tritt die Lieferadresse. Das greift auch
    // beim Zuklappen: Das reset() der Adresskomponente emittiert hier, und ab da gilt wieder die
    // Lieferadresse.
    this.subscriptions.add(this.orderProcessService.connectForm<AddressFormValue>(
      this.addressFormGroup.get('billingAddress') as FormGroup,
      state => state,
      (state, value) => ({
        ...state,
        addresses: {
          ...state.addresses,
          billingAddress: this.billsToDeliveryAddress() ? this.deliveryAddressGroup.getRawValue() : value,
          ...(this.mirrorsBillingToDelivery() ? {deliveryAddress: value} : {})
        }
      }),
      {rawValue: () => this.isBillingAddressActive()}
    ));

    // rawValue: die Lieferadresse wird für Bestandskunden nur zur Anzeige deaktiviert (readonly-Instanz der
    // Adresskomponente), gehört inhaltlich aber zur Bestellung. Ohne den Rohwert liefert der Emit aus deren
    // disable()-Kaskade einen Teilwert, der die geladene Adresse hier komplett überschreibt; in der Folge
    // fehlte sie in der Übersicht und es wurde kein Preis berechnet.
    this.subscriptions.add(this.orderProcessService.connectForm<AddressFormValue>(
      this.addressFormGroup.get('deliveryAddress') as FormGroup,
      state => state,
      (state, value) => ({
        ...state,
        addresses: {
          ...state.addresses,
          deliveryAddress: value,
          ...(this.billsToDeliveryAddress() ? {billingAddress: value} : {})
        }
      }),
      {rawValue: true}
    ));
  }

  private prefillAddressForm(userData: UserData | null) {
    // Formular vorbefüllen
    // noinspection GrazieInspection
    if (this.isPrefilled || !(userData?.userBackendId > 0)) { // !(>) muss so, weil sich <= im Fall von undefined anders verhält
      return;
    }
    let addressCode = userData.person?.addressCode;
    if (userData.person?.salutation && !userData.person?.addressCode) {
      this.clientTypes?.forEach(ct => {
        if (ct.key === userData.person?.salutation) {
          addressCode = ct.defaultAddressCodeId;
        }
      });
    }
    //Formular vorbefüllen
    if (this.addressCombination() === AddressCombination.BOTH || this.addressCombination() === AddressCombination.ONLY_DELIVERY) {
      this.prefillDeliveryOrBilling(userData, addressCode, "delivery");
    }
    if (this.addressCombination() === AddressCombination.BOTH || this.addressCombination() === AddressCombination.ONLY_BILLING) {
      this.prefillDeliveryOrBilling(userData, addressCode, "billing");
    }
    this.isPrefilled = true;
    this.publishPrefilledAddresses();
    // Die Vorbefüllung patcht ohne Events, statusChanges meldet den jetzt validen Stand also nicht.
    // Ohne diese Meldung bliebe der Step im linearen Stepper unerledigt: "Weiter" wäre klickbar,
    // stepper.next() täte aber nichts. Beim Printabo fiel das nicht auf, weil dort das disable() der
    // readonly-Maske zufällig ein Event auslöst - beim Digiabo (ONLY_BILLING) gibt es diese Maske nicht.
    this.stepCompleted.emit(this.addressFormGroup.status === 'VALID');
  }

  /**
   * Übernimmt die vorbefüllte Adresse in den Bestell-State.
   *
   * Nötig, weil die Vorbefüllung bewusst mit emitEvent:false patcht - sie soll keine Validierungs-
   * und Suchlawine auslösen. Damit erreicht sie aber auch connectForm nicht. Bislang kam die Adresse
   * nur zufällig in den State, nämlich über das valueChanges, das die readonly-Maske beim
   * Deaktivieren auslöst; blieb das aus, fehlte beim Bestandskunden die Lieferadresse - die
   * Übersicht zeigte sie nicht und es wurde kein Preis berechnet.
   *
   * Übertragen wird der Rohwert und nur, was auch abgefragt wird: ein nicht gerenderter oder
   * zugeklappter Block ist fachlich nicht vorhanden (P5W-4409).
   */
  private publishPrefilledAddresses(): void {
    const deliveryRequested = this.addressCombination() === AddressCombination.BOTH
      || this.addressCombination() === AddressCombination.ONLY_DELIVERY;
    const billingActive = this.isBillingAddressActive();
    const billingValue = billingActive ? this.addressFormGroup.get('billingAddress').getRawValue() : null;
    const deliveryValue = this.addressFormGroup.get('deliveryAddress').getRawValue();

    this.orderProcessService.update(state => ({
      ...state,
      addresses: {
        ...state.addresses,
        ...(deliveryRequested ? {deliveryAddress: deliveryValue} : {}),
        ...(billingActive ? {billingAddress: billingValue} : {}),
        ...(this.billsToDeliveryAddress() ? {billingAddress: deliveryValue} : {}),
        ...(billingActive && this.mirrorsBillingToDelivery() ? {deliveryAddress: billingValue} : {})
      }
    }));
  }

  /**
   * Trägt die Bestellung die Rechnungsadresse zugleich als Lieferadresse?
   *
   * Ja, wenn hier ausschließlich die Rechnungsadresse abgefragt wird - das ist der Fall des digitalen
   * Abos, das nicht zugestellt wird. Das Backend legt aus der Lieferadresse den SSO-Nutzer an
   * (SubscriptionController.updateAndValidateSubscriptionUnauthorized); ohne sie stünde ein Neukunde
   * ohne Namen da. Die Zusammenfassung verfährt genauso, wenn sie die Adresse selbst abfragt.
   */
  private mirrorsBillingToDelivery(): boolean {
    return this.addressCombination() === AddressCombination.ONLY_BILLING;
  }

  /**
   * Gilt die Lieferadresse zugleich als Rechnungsadresse?
   *
   * Ja beim Printabo (BOTH), solange "Abweichende Rechnungsadresse" nicht aktiviert ist. Der Bestell-
   * Request bekäme sie ohnehin (SubscriptionOrderConverter ersetzt eine leere Rechnungsadresse durch
   * die Lieferadresse) - im Bestell-State fehlte sie aber, und Bestellübersicht und Zusammenfassung
   * zeigten keine Rechnungsadresse.
   */
  private billsToDeliveryAddress(): boolean {
    return this.addressCombination() === AddressCombination.BOTH && !this.isBillingAddressActive();
  }

  private prefillDeliveryOrBilling(userData: any, addressCode: any, type: 'delivery' | 'billing'){
    // Das Control heißt birthDay - 'birthday' liefert null und die Vorbefüllung liefe ins Leere.
    this.addressFormGroup.get(`${type}Address.userData.birthDay`)?.patchValue(userData?.birthday ?? '', {emitEvent: false});
    this.addressFormGroup.get(`${type}Address.person.firstName`).patchValue(userData.person?.firstname ?? '', {emitEvent: false});
    this.addressFormGroup.get(`${type}Address.person.lastName`).patchValue(userData.person?.lastname ?? '', {emitEvent: false});
    this.addressFormGroup.get(`${type}Address.userData.email`).patchValue(userData.emailList[0] ?? '', {emitEvent: false});
    this.addressFormGroup.get(`${type}Address.person.title`).patchValue(userData.person?.title ?? '', {emitEvent: false});
    this.addressFormGroup.get(`${type}Address.person.salutation`).patchValue(addressCode ?? '', {emitEvent: false});
    this.addressFormGroup.get(`${type}Address.person.initials`).patchValue(userData.person?.initials ?? '', {emitEvent: false});

    this.addressFormGroup.get(`${type}Address.address.zipcode`).patchValue(userData.address?.zipcode ?? '', {emitEvent: false});
    if (userData.address?.country) {
      this.addressFormGroup.get(`${type}Address.address.country`).patchValue(userData.address?.country, {emitEvent: false});
    }
    this.addressFormGroup.get(`${type}Address.address.city`).patchValue(userData.address?.city ?? '', {emitEvent: false});
    this.addressFormGroup.get(`${type}Address.address.street`).patchValue(userData.address?.street ?? '', {emitEvent: false});

    const houseNo = userData.address?.houseno;
    if (houseNo) {
      this.addressFormGroup.get(`${type}Address.address.houseNo`).patchValue(HouseNo.getHouseNumber(houseNo), {emitEvent: false});
      this.addressFormGroup.get(`${type}Address.address.stiege`).patchValue(HouseNo.getStiege(houseNo), {emitEvent: false});
      this.addressFormGroup.get(`${type}Address.address.stock` ).patchValue(HouseNo.getStock (houseNo), {emitEvent: false});
      this.addressFormGroup.get(`${type}Address.address.tuer`  ).patchValue(HouseNo.getTuer  (houseNo), {emitEvent: false});
    }
  }

  protected areRelevantFormsValid(): boolean {
    const formDelivery = this.addressFormGroup.get('deliveryAddress');
    formDelivery?.enable({emitEvent: false});
    formDelivery?.updateValueAndValidity({emitEvent: false});
    let isValid = formDelivery?.valid;
    formDelivery?.disable({emitEvent: false});

    // isBillingAddressActive() statt des Controls 'showAddress': Bei ONLY_BILLING ist der Block
    // dauerhaft offen, ohne dass 'showAddress' je gesetzt wird - die Rechnungsadresse wurde dort
    // nicht mitvalidiert und der Weiter-Button blieb aktiv, obwohl Pflichtfelder fehlten.
    if (this.isBillingAddressActive()) {
      const formBilling = this.addressFormGroup.get('billingAddress');
      formBilling?.enable({emitEvent: false});
      formBilling?.updateValueAndValidity({emitEvent: false});
      isValid &&= formBilling?.valid;
      formBilling?.disable({emitEvent: false});
    }

    return isValid;
  }

  /**
   * Bekommt der Bestandskunde seine Stammadresse nur zur Ansicht vorgesetzt?
   *
   * Nein, wenn ausschließlich die Rechnungsadresse abgefragt wird (digitales Abo mit Adress-Step):
   * Dort ist die vorbefüllte Adresse des Kunden das einzige Adressfeld der Bestellung und muss
   * deshalb bearbeitbar bleiben. Die readonly-Maske zeigt die LIEFERadresse - die gibt es hier nicht.
   */
  protected showReadonlyCustomerAddress(): boolean {
    return this.userData()?.userBackendId > 0 && this.addressCombination() !== AddressCombination.ONLY_BILLING;
  }

  /**
   * Ist der Rechnungs-Adressblock aufgeklappt? Bei BOTH steuert das die Checkbox über das Control
   * 'showAddress'; bei ONLY_BILLING gibt es keine Checkbox, dort ist der Block dauerhaft offen und
   * 'showAddress' bleibt leer. Bei ONLY_DELIVERY wird der Block gar nicht gerendert.
   */
  protected isBillingAddressActive(): boolean {
    if (this.addressCombination() === AddressCombination.ONLY_BILLING) {
      return true;
    }
    if (this.addressCombination() !== AddressCombination.BOTH) {
      return false;
    }
    return !!this.addressFormGroup.get('billingAddress.showAddress')?.value;
  }

  /**
   * Ein zugeklappter Rechnungs-Adressblock ist fachlich nicht vorhanden und darf deshalb weder
   * validiert noch übertragen werden (P5W-4409). Angular nimmt deaktivierte Controls sowohl aus
   * 'valid' als auch aus 'value' heraus — das deckt beides mit einem Mechanismus ab, unabhängig
   * davon, welche Validatoren die geteilte Adress-Komponente auf den Kind-Controls hinterlässt.
   *
   * Das Control 'showAddress' bleibt bewusst aktiv: sonst wäre die Checkbox nicht mehr bedienbar und
   * die gesamte Gruppe DISABLED — was HupSubscriptionAddressComponent beim Init wieder aufmachen
   * würde. Umgeschaltet wird nur bei echtem Zustandswechsel und ohne Events, weil ein No-Op-
   * enable()/disable() sonst auf jedem Kind-Control ein valueChanges emittiert (s. P5W-4383); der
   * Status wird danach einmal gebündelt neu berechnet, damit statusChanges genau ein Ereignis liefert.
   */
  private applyBillingAddressActivation(): void {
    const active = this.isBillingAddressActive();
    let switched = false;

    BILLING_CONTENT_GROUPS.forEach(groupName => {
      const contentGroup = this.billingAddressGroup.get(groupName);
      if (!contentGroup || contentGroup.enabled === active) {
        return;
      }
      if (active) {
        contentGroup.enable({emitEvent: false});
      } else {
        contentGroup.disable({emitEvent: false});
      }
      switched = true;
    });

    if (switched) {
      this.addressFormGroup.updateValueAndValidity();
    }
  }

  private handleConfigurations() {
    // this.clientWithoutAddressAllowed.set(this.configService.getConfig('client.personWithoutAddressAllowed')?.value === '1');
    this.showWarningForOrderWithoutAddress.set(this.configService.getConfig('subscription.client-without-address.show-warning')?.value === '1');

    this.configService.loadConfig('requiredFields.addr.birthday').subscribe(config => {
      if (config?.value) {
        this.isBirthdayRequired.set(config.value === 'true' || config.value === '1');
        this.isBirthdayRequiredConst.set(this.isBirthdayRequired());
      }
    });

    //Soll das Telefonfeld eingeblendet werden?
    this.configService.loadConfig('offer.askForPhone').subscribe(config => {
      if (config?.value) {
        this.askForPhone.set(config.value === 'true' || config.value === '1');
      }
    });

    //Soll das Telefonfeld eine Pflichteingabe sein?
    this.configService.loadConfig('requiredFields.addr.phone').subscribe(config => {
      if (config?.value) {
        this.isPhoneRequired.set(config.value === 'true' || config.value === '1');

        if (this.addressCombination() == AddressCombination.BOTH || this.addressCombination() == AddressCombination.ONLY_DELIVERY) {
          this.setPhoneFieldsRequired('deliveryAddress');
        } else if (this.addressCombination() == AddressCombination.BOTH || this.addressCombination() == AddressCombination.ONLY_BILLING) {
          this.setPhoneFieldsRequired('billingAddress');
        }
      }
    });

    //Soll das Geburtstagsfeld eingeblendet werden?
    this.configService.loadConfig('offer.askForBirthday').subscribe(config => {
      if (config?.value) {
        this.askForBirthday.set(config.value === 'true' || config.value === '1');
        this.askForBirthdayConst.set(this.askForBirthday());
      }
    });

    //Soll das Geburtstagsfeld eine Pflichteingabe sein?
    this.configService.loadConfig('requiredFields.addr.birthday').subscribe(config => {
      if (config?.value) {
        this.isBirthdayRequired.set(config.value === 'true' || config.value === '1');
        this.isBirthdayRequiredConst.set(this.isBirthdayRequired());
      }
    });
  }

  private buildAddressForm(): void {
    this.addressFormGroup = this.formBuilder.group({
      deliveryAddress: this.formBuilder.group({
        userData: this.formBuilder.group({
          birthDay: [''],
          phone: [''],
          email: ['']
        }),
        showAddress: [''],
        address: this.formBuilder.group({
          country: [''],
          zipcode: [''],
          city: [''],
          street: [''],
          houseNo: [''],
          extraLine: [''],
          houseNoExt: [''],
          stiege: [''],
          stock: [''],
          tuer: [''],
          district: ['']
        }),
        person: this.formBuilder.group({
          title: [''],
          salutation: [''],
          firstName: [''],
          lastName: [''],
          initials: [''],
          middlename: ['']
        })
      }),
      billingAddress: this.formBuilder.group({
        userData: this.formBuilder.group({
          birthDay: [''],
          phone: [''],
          email: ['']
        }),
        showAddress: [''],
        address: this.formBuilder.group({
          country: [''],
          zipcode: [''],
          city: [''],
          street: [''],
          houseNo: [''],
          extraLine: [''],
          houseNoExt: [''],
          stiege: [''],
          stock: [''],
          tuer: [''],
          district: ['']
        }),
        person: this.formBuilder.group({
          title: [''],
          salutation: [''],
          firstName: [''],
          lastName: [''],
          initials: [''],
          middlename: [''],
        })
      }),
      email: ['', [Validators.required, Validators.pattern(ValidationPattern.EMAIL)]],
    });

    if (!this.hideLoginBox()) {
      // Das Feld wird ohne Login-Box nicht gerendert und ist damit keine Pflichtangabe. clearValidators()
      // rechnet den Status aber nicht neu — ohne updateValueAndValidity() bliebe das leere Control auf dem
      // bei der Erzeugung ermittelten INVALID stehen und der Adress-Step nie 'completed'.
      const emailControl = this.addressFormGroup.get('email');
      emailControl.clearValidators();
      emailControl.updateValueAndValidity({emitEvent: false});
    }
  }

  onSelectAddresses() {
    this.orderTrackingService.pushEvent(GtmEnum.ADDED_ADDRESSES, {});
  }

  get deliveryAddressGroup(): FormGroup {
    return this.addressFormGroup.get('deliveryAddress') as FormGroup;
  }

  get billingAddressGroup(): FormGroup {
    return this.addressFormGroup.get('billingAddress') as FormGroup;
  }

  //todo:
  setValidatorsAtForm(addressName: string, value: boolean) {
    /*if (value === true) {
      this.addressFormGroup.get(addressName + '.person.salutation').setValidators(Validators.required);
      this.addressFormGroup.get(addressName + '.person.firstName').setValidators(Validators.required);
      this.addressFormGroup.get(addressName + '.person.lastName').setValidators(Validators.required);
      this.addressFormGroup.get(addressName + '.address.country').setValidators(Validators.required);
      this.addressFormGroup.get(addressName + '.address.zipcode').setValidators(Validators.required);
      this.addressFormGroup.get(addressName + '.address.city').setValidators(Validators.required);
      this.addressFormGroup.get(addressName + '.address.street').setValidators(Validators.required);
      this.addressFormGroup.get(addressName + '.address.houseNo').setValidators(Validators.required);
    } else {
      this.addressFormGroup.get(addressName + '.person.salutation').clearValidators();
      this.addressFormGroup.get(addressName + '.person.firstName').clearValidators();
      this.addressFormGroup.get(addressName + '.person.lastName').clearValidators();
      this.addressFormGroup.get(addressName + '.address.country').clearValidators();
      this.addressFormGroup.get(addressName + '.address.zipcode').clearValidators();
      this.addressFormGroup.get(addressName + '.address.city').clearValidators();
      this.addressFormGroup.get(addressName + '.address.street').clearValidators();
      this.addressFormGroup.get(addressName + '.address.houseNo').clearValidators();

      this.addressFormGroup.get(addressName + '.person.salutation').updateValueAndValidity();
      this.addressFormGroup.get(addressName + '.person.firstName').updateValueAndValidity();
      this.addressFormGroup.get(addressName + '.person.lastName').updateValueAndValidity();
      this.addressFormGroup.get(addressName + '.address.country').updateValueAndValidity();
      this.addressFormGroup.get(addressName + '.address.zipcode').updateValueAndValidity();
      this.addressFormGroup.get(addressName + '.address.city').updateValueAndValidity();
      this.addressFormGroup.get(addressName + '.address.street').updateValueAndValidity();
      this.addressFormGroup.get(addressName + '.address.houseNo').updateValueAndValidity();
    }*/
  }

  private handleBaseData() {
    this.salutations = this.baseDataService.getBaseData('addresscodes');
    this.clientTypes = this.baseDataService.getBaseData('clienttypes');
  }

  /**
   * prüft, ob ein User angemeldet ist und wenn nein (Neukunde) werden die Felder Birthday und Phone je nach Konfiguration auf Required
   * gesetzt. Felder sollen überhaupt nur required sein, wenn man nicht angemeldet ist, sonst bleibt der Bestandskunde bei der Bestellung
   * stecken. Außerdem, nur wenn BEIDE booleans auf true gesetzt sind Felder required machen.
   * Wenn ein E-Paper vorliegt, müssen andere Felder gesetzt werden.
   */
  evaluatePhoneAndBirthdayFields(offer: Offer) {
    /*if (!this.isAuthenticated && offer.offerType != undefined) {
      if (offer.offerType.ePaper) {
        if (this.isPhoneRequired && this.askForPhone) {
          this.setPhoneFieldsRequired('billingAddress');
        }
      } else if(!offer.offerType.ePaper){
        if (this.isPhoneRequired && this.askForPhone) {
          this.setPhoneFieldsRequired('deliveryAddress');
        }
        // if (this.isBirthdayRequired && this.askForBirthday) {
        //   this.addressFormGroup.get('deliveryAddress.userData().birthDay').setValidators(Validators.required);
        }
      }
    }*/
  }

  setAsyncValidators(): void {
    this.addressFormGroup.get('email').setAsyncValidators(CustomValidators.checkEmailAddress(
      this.http,
      this.translationService,
    ));
  }

  private setPhoneFieldsRequired(form: string) {
    this.addressFormGroup?.get(form + '.userData?.phone')?.addValidators(Validators.required);
    this.addressFormGroup?.get(form + '.userData?.phone')?.updateValueAndValidity();
  }

  protected readonly AddressCombination = AddressCombination;
  protected readonly JSON = JSON;
}
